import type { FC } from 'react';

import AlertCircle from '@crehana/compass.icons.outline/AlertCircle';
import Refresh from '@crehana/compass.icons.outline/Refresh';
import { Button } from '@crehana/compass.ui/button';
import { cn } from '@crehana/compass.ui/v2';

import { useTranslationCreateAndEditEmailTemplates } from '@/shared/components/email-component/hooks/use-translations';

// Definition of the ErrorExtension component
export const ErrorExtension: FC<{
  textDark?: boolean; // Optional prop to determine if the text should be dark or light
  onClickReset: () => void; // Function for the reset/cancel button
  onClickRetry: () => void; // Function for the retry button
}> = ({ textDark, onClickReset, onClickRetry }) => {
  const { t } = useTranslationCreateAndEditEmailTemplates(); // Hook for translation

  return (
    <>
      {/* Error message with alert icon */}
      <p
        className={cn(
          'tw-mt-1.2  tw-font-link1 tw-font-semibold tw-gap-0.4 tw-flex tw-items-center', // Styling classes
          textDark ? 'tw-text-alert-300' : 'tw-text-alert-500', // Changes text color based on the textDark prop
        )}
      >
        <AlertCircle size={16} className="tw-text-current" /> {/* Alert icon */}
        {t('NOTIFICATION_ERROR_LABEL')} {/* Translated error message */}
      </p>

      {/* Container for action buttons */}
      <div className="tw-mt-1.2 tw-flex tw-flex-col tw-gap-1.2 tw-items-center">
        {/* Retry button */}
        <Button
          type="button"
          size="xs"
          variant="primary"
          className="tw-bg-primary-dark-600" // Custom style for the button
          onClick={e => {
            e.preventDefault(); // Prevents the default button behavior
            onClickRetry(); // Calls the onClickRetry function
          }}
        >
          <>
            <Refresh className="tw-fill" size={16} /> {/* Refresh icon */}
            {t('NOTIFICATION_RETRY')} {/* Translated retry text */}
          </>
        </Button>

        {/* Cancel/reset button */}
        <button
          type="button"
          className={cn(
            'tw-w-fit tw-btn-tertiary-xs', // Basic style for the button
            textDark ? 'tw-text-neutral-dark-400' : 'tw-text-neutral-light-700', // Changes the text color based on the textDark prop
          )}
          onClick={e => {
            e.preventDefault(); // Prevents the default button behavior
            onClickReset(); // Calls the onClickReset function
          }}
        >
          {t('USER_DIALOG_DELETE_BUTTON_CANCEL')}{' '}
          {/* Translated cancel button text */}
        </button>
      </div>
    </>
  );
};
